/** Tour operator → Defaults (`/tour-operators/:id/defaults[/tag-categories|/trip-settings]`).
 *
 *  Two sections with two independent saves (backend plan 2026-10-07): the operator's
 *  default tag categories (M2M rows + a version token) and the config-stored trip settings
 *  (`config` + its change timestamp). One section is edited at a time — the other stays
 *  readable, its Edit disabled with the reason — so there is one draft, one ⌘S and one
 *  navigation guard on the page.
 *
 *  The guard is the tab's (a data router consults only one blocker): moving between the
 *  two sections' URLs is free, leaving the tab with unsaved changes asks first. A section
 *  URL scrolls that section into view once both sections have loaded (the first one's
 *  height is not known before). */
import { Box, Divider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { TourOperatorDetail } from '@/api/tourOperators';
import { usePermissions } from '@/hooks/usePermissions';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';

import { DefaultTagCategoriesSection } from '../defaults/DefaultTagCategoriesSection';
import {
  DEFAULTS_SECTIONS,
  sectionDomId,
  type DefaultsSectionId,
} from '../defaults/DefaultsSection';
import { useDefaultTagCategories, useTripDefaults } from '../defaults/queries';
import { TripSettingsSection } from '../defaults/TripSettingsSection';
import { operatorDefaultsHref } from '../tenantLinks';

const TITLES: Record<DefaultsSectionId, string> = {
  'tag-categories': 'the default tag categories',
  'trip-settings': 'the trip settings',
};

interface Props {
  to: TourOperatorDetail;
  /** URL segments after ".../defaults": [section?]. */
  path: string[];
}

export function DefaultsTab({ to, path }: Props) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_top_manage');

  const [editing, setEditing] = useState<DefaultsSectionId | null>(null);
  const [dirty, setDirty] = useState<Record<DefaultsSectionId, boolean>>({
    'tag-categories': false,
    'trip-settings': false,
  });
  const anyDirty = editing !== null && dirty[editing];
  const { prompt } = useUnsavedChangesPrompt(anyDirty, {
    basePath: operatorDefaultsHref(to.id),
    message: `You have unsaved changes to ${editing ? TITLES[editing] : 'the defaults'}. Leaving now will discard them.`,
  });

  // Stable per section, so the sections' "report dirty" effects run only on a change.
  const onDirty = useRef({
    'tag-categories': (d: boolean) => setDirty((s) => ({ ...s, 'tag-categories': d })),
    'trip-settings': (d: boolean) => setDirty((s) => ({ ...s, 'trip-settings': d })),
  }).current;
  const onEditing = useRef({
    'tag-categories': (on: boolean) => setEditing(on ? 'tag-categories' : null),
    'trip-settings': (on: boolean) => setEditing(on ? 'trip-settings' : null),
  }).current;
  const blockedReason = (id: DefaultsSectionId) =>
    editing && editing !== id ? `Save or discard ${TITLES[editing]} first` : null;

  // A section URL scrolls to its section once the content above it has loaded — on every
  // navigation to it (a header's link clicked again scrolls again), not on refetches.
  const section = DEFAULTS_SECTIONS.find((s) => s === path[0]) ?? null;
  const { key: locationKey } = useLocation();
  const tagCategories = useDefaultTagCategories(to.id);
  const tripSettings = useTripDefaults(to.id);
  const ready = !tagCategories.isPending && !tripSettings.isPending;
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!section || !ready || scrolledFor.current === locationKey) return;
    scrolledFor.current = locationKey;
    document.getElementById(sectionDomId(section))?.scrollIntoView?.({ block: 'start' });
  }, [section, ready, locationKey]);

  return (
    <Box sx={{ display: 'grid', gap: 4, minWidth: 0 }} data-defaults-tab>
      {prompt}
      <DefaultTagCategoriesSection
        to={to}
        canManage={canManage}
        editing={editing === 'tag-categories'}
        editBlockedReason={blockedReason('tag-categories')}
        onEditingChange={onEditing['tag-categories']}
        onDirtyChange={onDirty['tag-categories']}
      />
      <Divider />
      <TripSettingsSection
        to={to}
        canManage={canManage}
        editing={editing === 'trip-settings'}
        editBlockedReason={blockedReason('trip-settings')}
        onEditingChange={onEditing['trip-settings']}
        onDirtyChange={onDirty['trip-settings']}
      />
    </Box>
  );
}
